import React, { useMemo, useState, useRef, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Archive, Plus, Pencil, Trash2, Search, Camera, X, MapPin, QrCode } from "lucide-react";
import { useApp, formatBRL } from "../context/AppContext";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useToast } from "../hooks/use-toast";
import { gerarEtiquetasPatrimonio } from "../utils/etiquetasPdf";

const inputCls = "w-full px-3.5 py-2.5 rounded-lg border border-slate-200 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 text-sm";
const CATEGORIAS = ["Móvel", "Equipamento", "Eletrodoméstico", "Outro"];
const ESTADOS = ["Novo", "Bom", "Regular", "Ruim"];
const estadoCls = { Novo: "text-emerald-700 bg-emerald-50", Bom: "text-blue-700 bg-blue-50", Regular: "text-amber-700 bg-amber-50", Ruim: "text-red-700 bg-red-50" };
const vazio = { nome: "", categoria: "Móvel", patrimonio: "", localizacao: "", quantidade: 1, estado: "Bom", dataAquisicao: "", valor: "", foto: "", observacoes: "" };

const Inventario = () => {
  const { inventario, salvarInventario, excluirInventario, empresa } = useApp();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(vazio);
  const [q, setQ] = useState("");
  const [fcat, setFcat] = useState("todas");
  const fileRef = useRef(null);
  const { id: routeId } = useParams();
  const navigate = useNavigate();
  const [detalhe, setDetalhe] = useState(null);

  // Abre a ficha do item ao acessar via QR Code (/inventario/:id)
  useEffect(() => {
    if (routeId && (inventario || []).length) {
      const item = inventario.find((i) => i.id === routeId);
      if (item) setDetalhe(item);
    }
  }, [routeId, inventario]);

  const abrirNovo = () => { setEditing(null); setForm(vazio); setOpen(true); };
  const abrirEdit = (i) => { setEditing(i); setForm({ ...i, valor: i.valor || "" }); setOpen(true); };

  const handleFoto = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, foto: reader.result }));
    reader.readAsDataURL(file);
  };

  const salvar = async () => {
    if (!form.nome.trim()) { toast({ title: "Informe o nome do item" }); return; }
    const payload = { ...form, quantidade: Number(form.quantidade) || 1, valor: parseFloat(form.valor) || 0 };
    try {
      await salvarInventario(editing ? { ...payload, id: editing.id } : payload);
      toast({ title: editing ? "Item atualizado" : "Item cadastrado" });
      setOpen(false);
    } catch (e) { toast({ title: "Erro ao salvar", description: e?.response?.data?.detail || "" }); }
  };
  const remover = async (i) => { await excluirInventario(i.id); toast({ title: "Item excluído" }); };

  const gerarEtiquetas = async (itens) => {
    if (!itens.length) { toast({ title: "Nenhum item para etiquetar" }); return; }
    toast({ title: "Gerando etiquetas..." });
    await gerarEtiquetasPatrimonio({ empresa, itens });
  };

  const lista = useMemo(() => (inventario || []).filter((i) =>
    (fcat === "todas" || i.categoria === fcat) &&
    (i.nome.toLowerCase().includes(q.toLowerCase()) || (i.patrimonio || "").toLowerCase().includes(q.toLowerCase()) || (i.localizacao || "").toLowerCase().includes(q.toLowerCase()))
  ), [inventario, q, fcat]);

  const totalItens = (inventario || []).reduce((s, i) => s + (Number(i.quantidade) || 0), 0);
  const valorTotal = (inventario || []).reduce((s, i) => s + (Number(i.valor) || 0) * (Number(i.quantidade) || 0), 0);

  return (
    <div className="space-y-6" data-testid="inventario-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Inventário</h1>
          <p className="text-slate-400 text-sm mt-0.5">Móveis, equipamentos e patrimônio da empresa</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-2" onClick={() => gerarEtiquetas(lista)} data-testid="etiquetas-btn"><QrCode className="w-4 h-4" /> Etiquetas QR</Button>
          <Button onClick={abrirNovo} className="bg-blue-600 hover:bg-blue-700 gap-2" data-testid="novo-inventario-btn"><Plus className="w-4 h-4" /> Novo Item</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-slate-100 p-5 shadow-sm">
          <p className="text-sm text-slate-400">Itens cadastrados</p>
          <p className="text-2xl font-bold text-slate-800 mt-1" data-testid="inv-total-itens">{totalItens}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-100 p-5 shadow-sm">
          <p className="text-sm text-slate-400">Valor total do patrimônio</p>
          <p className="text-2xl font-bold text-emerald-600 mt-1" data-testid="inv-valor-total">{formatBRL(valorTotal)}</p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg bg-white border border-slate-200 max-w-sm flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nome, patrimônio ou local..." className="flex-1 bg-transparent outline-none text-sm text-slate-700" data-testid="inventario-busca" />
        </div>
        <div className="w-48">
          <Select value={fcat} onValueChange={setFcat}>
            <SelectTrigger data-testid="filtro-categoria-inv"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="todas">Todas as categorias</SelectItem>{CATEGORIAS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {lista.length === 0 && <div className="col-span-full bg-white rounded-xl border border-slate-100 p-10 text-center text-slate-400">Nenhum item encontrado.</div>}
        {lista.map((i) => (
          <div key={i.id} className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden" data-testid={`inventario-card-${i.id}`}>
            <div className="flex items-center gap-3 p-4">
              {i.foto ? <img src={i.foto} alt={i.nome} className="w-14 h-14 rounded-lg object-cover" />
                : <div className="w-14 h-14 rounded-lg bg-slate-100 flex items-center justify-center"><Archive className="w-6 h-6 text-slate-300" /></div>}
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-800 truncate">{i.nome}</p>
                <p className="text-xs text-slate-400">{i.categoria}{i.patrimonio ? ` · Patr. ${i.patrimonio}` : ""}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${estadoCls[i.estado] || "text-slate-600 bg-slate-100"}`}>{i.estado}</span>
                  <span className="text-[11px] text-slate-500">Qtd: {i.quantidade}</span>
                </div>
              </div>
            </div>
            <div className="px-4 pb-3 text-xs text-slate-500 space-y-1">
              {i.localizacao && <p className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {i.localizacao}</p>}
              <p>Valor unit.: <span className="font-medium text-slate-700">{formatBRL(i.valor)}</span></p>
            </div>
            <div className="flex border-t border-slate-100">
              <button onClick={() => gerarEtiquetas([i])} className="flex-1 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50 flex items-center justify-center gap-1" data-testid={`etiqueta-inventario-${i.id}`}><QrCode className="w-3.5 h-3.5" /> Etiqueta</button>
              <button onClick={() => abrirEdit(i)} className="flex-1 py-2 text-xs font-medium text-blue-700 hover:bg-blue-50 flex items-center justify-center gap-1 border-l border-slate-100" data-testid={`editar-inventario-${i.id}`}><Pencil className="w-3.5 h-3.5" /> Editar</button>
              <button onClick={() => remover(i)} className="flex-1 py-2 text-xs font-medium text-red-600 hover:bg-red-50 flex items-center justify-center gap-1 border-l border-slate-100" data-testid={`excluir-inventario-${i.id}`}><Trash2 className="w-3.5 h-3.5" /> Excluir</button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent aria-describedby={undefined} className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="inventario-modal">
          <DialogHeader><DialogTitle>{editing ? "Editar Item" : "Novo Item"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="relative w-16 h-16 rounded-xl overflow-hidden bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0">
                {form.foto ? <img src={form.foto} alt="foto" className="w-full h-full object-cover" /> : <Archive className="w-6 h-6 text-slate-300" />}
                {form.foto && <button type="button" onClick={() => setForm({ ...form, foto: "" })} className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center"><X className="w-3 h-3" /></button>}
              </div>
              <div>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFoto} data-testid="inventario-foto-input" />
                <Button type="button" variant="outline" className="gap-2" onClick={() => fileRef.current?.click()}><Camera className="w-4 h-4" /> Foto</Button>
              </div>
            </div>
            <div><label className="text-sm font-medium text-slate-600 mb-1 block">Nome do item</label>
              <input className={inputCls} value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="inventario-nome" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium text-slate-600 mb-1 block">Categoria</label>
                <Select value={form.categoria} onValueChange={(v) => setForm({ ...form, categoria: v })}>
                  <SelectTrigger data-testid="inventario-categoria"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIAS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><label className="text-sm font-medium text-slate-600 mb-1 block">Nº Patrimônio</label>
                <input className={inputCls} value={form.patrimonio} onChange={(e) => setForm({ ...form, patrimonio: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium text-slate-600 mb-1 block">Localização</label>
                <input className={inputCls} value={form.localizacao} onChange={(e) => setForm({ ...form, localizacao: e.target.value })} placeholder="ex.: Quarto 03, Recepção" data-testid="inventario-localizacao" /></div>
              <div><label className="text-sm font-medium text-slate-600 mb-1 block">Quantidade</label>
                <input type="number" min="1" className={inputCls} value={form.quantidade} onChange={(e) => setForm({ ...form, quantidade: e.target.value })} data-testid="inventario-quantidade" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium text-slate-600 mb-1 block">Estado</label>
                <Select value={form.estado} onValueChange={(v) => setForm({ ...form, estado: v })}>
                  <SelectTrigger data-testid="inventario-estado"><SelectValue /></SelectTrigger>
                  <SelectContent>{ESTADOS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><label className="text-sm font-medium text-slate-600 mb-1 block">Valor unitário (R$)</label>
                <input type="number" step="0.01" className={inputCls} value={form.valor} onChange={(e) => setForm({ ...form, valor: e.target.value })} data-testid="inventario-valor" /></div>
            </div>
            <div><label className="text-sm font-medium text-slate-600 mb-1 block">Data de aquisição</label>
              <input type="date" className={inputCls} value={form.dataAquisicao} onChange={(e) => setForm({ ...form, dataAquisicao: e.target.value })} /></div>
            <div><label className="text-sm font-medium text-slate-600 mb-1 block">Observações</label>
              <textarea rows={2} className={inputCls} value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={salvar} data-testid="salvar-inventario">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ficha do item (aberta via QR Code) */}
      <Dialog open={!!detalhe} onOpenChange={(o) => { if (!o) { setDetalhe(null); if (routeId) navigate("/inventario"); } }}>
        <DialogContent aria-describedby={undefined} className="sm:max-w-md" data-testid="inventario-detalhe-modal">
          <DialogHeader><DialogTitle>Ficha do Patrimônio</DialogTitle></DialogHeader>
          {detalhe && (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                {detalhe.foto ? <img src={detalhe.foto} alt={detalhe.nome} className="w-20 h-20 rounded-lg object-cover" />
                  : <div className="w-20 h-20 rounded-lg bg-slate-100 flex items-center justify-center"><Archive className="w-8 h-8 text-slate-300" /></div>}
                <div>
                  <p className="text-lg font-bold text-slate-800">{detalhe.nome}</p>
                  <p className="text-sm text-slate-400">{detalhe.categoria}</p>
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${estadoCls[detalhe.estado] || "text-slate-600 bg-slate-100"}`}>{detalhe.estado}</span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><p className="text-slate-400 text-xs">Nº Patrimônio</p><p className="font-medium text-slate-700">{detalhe.patrimonio || "-"}</p></div>
                <div><p className="text-slate-400 text-xs">Localização</p><p className="font-medium text-slate-700">{detalhe.localizacao || "-"}</p></div>
                <div><p className="text-slate-400 text-xs">Quantidade</p><p className="font-medium text-slate-700">{detalhe.quantidade}</p></div>
                <div><p className="text-slate-400 text-xs">Valor unitário</p><p className="font-medium text-slate-700">{formatBRL(detalhe.valor)}</p></div>
                {detalhe.dataAquisicao && <div><p className="text-slate-400 text-xs">Aquisição</p><p className="font-medium text-slate-700">{detalhe.dataAquisicao.split("-").reverse().join("/")}</p></div>}
              </div>
              {detalhe.observacoes && <div><p className="text-slate-400 text-xs">Observações</p><p className="text-sm text-slate-700">{detalhe.observacoes}</p></div>}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDetalhe(null); if (routeId) navigate("/inventario"); }}>Fechar</Button>
            {detalhe && <Button className="bg-blue-600 hover:bg-blue-700 gap-2" onClick={() => { const it = detalhe; setDetalhe(null); if (routeId) navigate("/inventario"); abrirEdit(it); }}><Pencil className="w-4 h-4" /> Editar</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Inventario;
